// Fashion & Beauty V1: Owner archive / restore of departments + per-store category management.
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Archive, ArchiveRestore, Plus, Pencil, X } from "lucide-react";
import { useFashion } from "@/lib/fashion";
import { useAuth } from "@/context/AuthContext";

function CategoryChip({ dept, cat, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(cat.name);
  const save = async () => {
    try { await api.put(`/departments/${dept.key}/categories/${cat.id}`, { name }); toast.success("Category renamed"); setEdit(false); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/departments/${dept.key}/categories/${cat.id}`); toast.success("Category removed"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (edit) return (
    <span className="inline-flex items-center gap-1" data-testid={`cat-edit-${cat.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-7 w-36 text-xs" data-testid={`cat-edit-input-${cat.id}`} />
      <Button size="sm" className="h-7 px-2" onClick={save} data-testid={`cat-edit-save-${cat.id}`}>Save</Button>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEdit(false)}>Cancel</Button>
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/50 pl-3 pr-1 py-0.5 text-xs" data-testid={`cat-chip-${cat.id}`}>
      {cat.name}
      {!dept.archived && <>
        <button type="button" className="p-1 text-muted-foreground hover:text-foreground" onClick={() => setEdit(true)} title="Rename" data-testid={`cat-rename-${cat.id}`}><Pencil className="w-3 h-3" /></button>
        <button type="button" className="p-1 text-muted-foreground hover:text-rose-500" onClick={remove} title="Remove" data-testid={`cat-delete-${cat.id}`}><X className="w-3 h-3" /></button>
      </>}
    </span>
  );
}

function DepartmentCard({ dept, isOwner, onChanged }) {
  const [newCat, setNewCat] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post(`/departments/${dept.key}/categories`, { name: newCat.trim() }); setNewCat(""); toast.success("Category added"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const archive = async () => {
    setBusy(true);
    try { await api.post(`/departments/${dept.key}/archive`, { reason }); toast.success(`${dept.name} archived — products and history kept`); setArchiveOpen(false); setReason(""); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try { await api.post(`/departments/${dept.key}/restore`); toast.success(`${dept.name} restored`); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className={`p-4 space-y-3 ${dept.archived ? "opacity-80 border-dashed" : ""}`} data-testid={`dept-card-${dept.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-head font-bold text-lg" data-testid={`dept-name-${dept.key}`}>{dept.name}</p>
          <p className="text-xs text-muted-foreground">
            {dept.attributes.length ? `Variants by ${dept.attributes.map((a) => a.label.toLowerCase()).join(", ")}` : "Any simple attributes"} · sold per {dept.unit}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={dept.archived ? "text-amber-600 border-amber-500/40" : "text-accent border-accent/40"} data-testid={`dept-status-${dept.key}`}>{dept.archived ? "Archived" : "Active"}</Badge>
          {isOwner && (dept.archived
            ? <Button size="sm" variant="outline" disabled={busy} onClick={restore} data-testid={`dept-restore-${dept.key}`}><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
            : <Button size="sm" variant="outline" disabled={busy} onClick={() => setArchiveOpen(true)} data-testid={`dept-archive-${dept.key}`}><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>)}
        </div>
      </div>
      {dept.archived && <p className="text-xs text-amber-600 dark:text-amber-400" data-testid={`dept-archived-note-${dept.key}`}>Hidden from the register and new sales. Products, stock and history are preserved; historical returns still work. Restore any time.</p>}
      <div className="flex flex-wrap gap-1.5" data-testid={`dept-categories-${dept.key}`}>
        {dept.categories.map((c) => <CategoryChip key={c.id} dept={dept} cat={c} onChanged={onChanged} />)}
        {dept.categories.length === 0 && <span className="text-xs text-muted-foreground">No categories yet.</span>}
      </div>
      {!dept.archived && (
        <div className="flex gap-2 max-w-sm">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="New category" className="h-9" data-testid={`dept-newcat-${dept.key}`} />
          <Button size="sm" variant="outline" className="h-9" onClick={addCat} data-testid={`dept-addcat-${dept.key}`}><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      )}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent className="max-w-sm" data-testid={`dept-archive-dialog-${dept.key}`}>
          <DialogHeader><DialogTitle className="font-head">Archive {dept.name}?</DialogTitle>
            <DialogDescription>The department leaves the operational view. Nothing is deleted: products, stock records, transactions and history stay, and returns on past sales still work. You can restore it any time.</DialogDescription></DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="dept-archive-reason" />
          <Button onClick={archive} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dept-archive-confirm">Archive department</Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function DepartmentsPanel() {
  const { user } = useAuth();
  const { departments, reload, ready } = useFashion();
  if (!ready) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="departments-panel">
      <p className="text-sm text-muted-foreground">Departments are enabled for your store by NEEC. {user?.role === "owner" ? "You can archive a department you no longer sell and restore it later; " : ""}each store manages its own categories.</p>
      {departments.map((d) => <DepartmentCard key={d.key} dept={d} isOwner={user?.role === "owner"} onChanged={reload} />)}
      {departments.length === 0 && <p className="text-muted-foreground" data-testid="departments-empty">No departments enabled yet. Contact NEEC to enable Clothing, Shoes, Hair retail, Beauty products or General Retail.</p>}
    </div>
  );
}
